// AutoPay / UPI e-Mandate API layer.
//
// Straight port of DSARegistrationAPI.js's mandate calls, re-wired to this
// repo's thunk. The original used `store.dispatch(linkTokenPost(...))`; the
// equivalent here is `store.dispatch(dynamicTokenPost(...))` — same contract:
// POST `url` with a FormData body built from `FormDataDynamic`, and the
// resolved action's `.payload` is the house envelope `{ success, data,
// message }`.
import store from "../../Redux/Store/Store";
import { dynamicTokenPost } from "../../Redux/Features/Slices/DynamicTokenPostSlice";
import { getData } from "../../Redux/Features/Slices/GetDataSlice";

// All three routes are the originals from DSARegistrationAPI.js.
// ---------------------------------------------------------------------------
export const MANDATE_ENDPOINTS = {
  create: "UPIAPI/create-mandate",
  status: "UPIAPI/mandate-status-by-criteria",
  getByEmployee: "UPIAPI/GetMandateByEmployeeId",
  revoke: "UPIAPI/revoke-mandate",
  sendQrWhatsApp: "CamNoteDetail/SendPaymentQRCodeOnWhatsAppToCustomer",
  setupStatus: "UPIAPI/mandate-setup-status",
};

// Values sent to create-mandate (backend guide S1).
// ⚠️ `amount` is the UPI mandate ceiling — a HIDDEN constant. The guide is
// explicit that it must never be shown on screen; 0/empty makes the
// backend reject the call with 400 "Invalid amount specified.".
export const MANDATE_PLAN = {
  planName: "Subscription",
  remark: "AutoPay",
  validityYears: 10, // ValidityEndDate = today + 10 years
  planFee: 50, // per-day plan fee (Rate master PerDayRate)
  amount: 5000, // hidden mandate limit — never render this
  // display-only
  dailyChargeLabel: "₹50",
};

// yyyy-MM-dd in local time (toISOString would shift to UTC and can land
// on the previous day for IST mornings).
const toApiDate = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// FormData values must be primitives; drop null/undefined so [FromForm]
// binding gets clean empty-ness rather than the string "null".
const cleanParams = (params = {}) => {
  const out = {};
  Object.keys(params).forEach((k) => {
    const v = params[k];
    out[k] = v === null || v === undefined ? "" : v;
  });
  return out;
};

const post = async (url, params = {}) => {
  const res = await store.dispatch(
    dynamicTokenPost({ url, FormDataDynamic: cleanParams(params) })
  );
  return res?.payload;
};

// create-mandate only: `getData` resolves non-2xx answers to the backend
// envelope too (rejectWithValue), so the 409 "already active" and the bank
// failure message ("We couldn't generate the AutoPay QR right now…") reach
// the screen as-is instead of collapsing into a generic error.
const postWithEnvelope = async (url, params = {}) => {
  const res = await store.dispatch(
    getData({ url, FormDataDynamic: cleanParams(params) })
  );
  return res?.payload;
};

// ---------------------------------------------------------------------------
// Calls — same shapes DSARegistrationAPI.js exposed, so the ported hook works
// unchanged: { success, message, data: { merchantTranId, qrString, amount, refId } }
// ---------------------------------------------------------------------------

export const createDSAMandate = async ({ employeeId, dateOfJoining }) => {
  try {
    const now = new Date();
    const validityStart = toApiDate(now);
    const validityEnd = toApiDate(
      new Date(now.getFullYear() + MANDATE_PLAN.validityYears, now.getMonth(), now.getDate())
    );

    // Plan effective from the DSA's date of joining (falls back to today if
    // the hire date isn't cached).
    const joining = dateOfJoining ? new Date(dateOfJoining) : null;
    const planEffectiveFrom =
      joining && !Number.isNaN(joining.getTime()) ? toApiDate(joining) : validityStart;

    return await postWithEnvelope(MANDATE_ENDPOINTS.create, {
      Remark: MANDATE_PLAN.remark,
      PlanEffectiveFrom: planEffectiveFrom,
      EmployeeId: employeeId,
      DateOfJoining: planEffectiveFrom,
      PlanName: MANDATE_PLAN.planName,
      ValidityEndDate: validityEnd,
      Amount: MANDATE_PLAN.amount,
      PlanFee: MANDATE_PLAN.planFee,
      ValidityStartDate: validityStart,
    });
  } catch (error) {
    console.error("Create Mandate API Error:", error);
    return {
      success: false,
      message: error?.response?.data?.message || "Something went wrong",
    };
  }
};

// Payer approval ke baad status check — ise poll kiya jaata hai.
// `TransactionType` = "M" (mandate) — ICICI ke mandate-status-by-criteria
// endpoint ke liye required hai.
export const getDSAMandateStatus = async ({ merchantTranId }) => {
  try {
    return await post(MANDATE_ENDPOINTS.status, {
      TransactionType: "M",
      MerchantTranId: merchantTranId,
    });
  } catch (error) {
    console.error("Mandate Status API Error:", error);
    return { success: false, message: "Something went wrong" };
  }
};

// Employee ka already-existing mandate record(s) — page load pe (aur revoke
// ke baad) latest status/UMN/dates laane ke liye. Query-string GET-jaisa call
// hai (empty body), isliye `getData` thunk (create/status wale FormData
// `post` helper se alag).
export const getMandateByEmployeeId = async ({ employeeId }) => {
  try {
    const res = await store.dispatch(
      getData({
        url: `${MANDATE_ENDPOINTS.getByEmployee}?employeeId=${employeeId}`,
        FormDataDynamic: {},
      })
    );
    return res?.payload;
  } catch (error) {
    console.error("Get Mandate By EmployeeId API Error:", error);
    return { success: false, message: "Something went wrong" };
  }
};

// Revoke an active mandate. `umn` identifies the mandate; `collectByDate`
// tells the bank not to collect after that point (we send "now" — stop
// immediately).
export const revokeDSAMandate = async ({ umn, remark, payerVA, collectByDate }) => {
  try {
    return await post(MANDATE_ENDPOINTS.revoke, {
      UMN: umn,
      Remark: remark,
      PayerVA: payerVA,
      CollectByDate: collectByDate || new Date().toISOString(),
    });
  } catch (error) {
    console.error("Revoke Mandate API Error:", error);
    return {
      success: false,
      message: error?.response?.data?.message || "Something went wrong",
    };
  }
};

// Checked right after login — decides whether the DSA is sent to the
// AutoPay setup screen or straight to the Dashboard. `data.isActive`
// (true/false) is the sole signal read out of the response.
export const getMandateSetupStatus = async ({ employeeId }) => {
  try {
    const res = await store.dispatch(
      getData({
        url: `${MANDATE_ENDPOINTS.setupStatus}?employeeId=${employeeId}`,
        FormDataDynamic: {},
      })
    );
    return res?.payload;
  } catch (error) {
    console.error("Get Mandate Setup Status API Error:", error);
    return { success: false, message: "Something went wrong" };
  }
};

// Send the mandate QR to the DSA's registered WhatsApp number.
export const sendPaymentQRCodeOnWhatsApp = async ({
  customerName,
  customerWhatsAppNo,
  qrString,
}) => {
  try {
    return await post(MANDATE_ENDPOINTS.sendQrWhatsApp, {
      PackageId: 0,
      CustomerName: customerName,
      CustomerWhatsAppNo: customerWhatsAppNo,
      QRString: qrString,
    });
  } catch (error) {
    console.error("Send Payment QR on WhatsApp API Error:", error);
    return { success: false, message: "Something went wrong" };
  }
};
